import { Button, Card, Typography, Image, Spin } from 'antd';
import {
  BarChart2,
  TrendingUp,
  Newspaper,
  PieChart,
  Target,
  Users,
  Clock,
  Mail,
  Bot,
  Sparkles,
  Building,
  AlertCircle,
} from 'lucide-react';
import './App.css';

import placeholder1 from './assets/placeholder-1.png';
import placeholder2 from './assets/placeholder-2.png';
import placeholder4 from './assets/placeholder-4.png';
import placeholder5 from './assets/placeholder-5.png';
import placeholder6 from './assets/placeholder-6.png';
import placeholder8 from './assets/placeholder-8.png';
import demoVideo from './assets/ttdemo.mp4';
import metroVideo from './assets/metro_compressed.mp4';
import metroPoster from './assets/metro.png';
import extendedImage from './assets/extended900.png';
import { useState, useRef, useEffect, RefObject } from 'react';

const { Title, Paragraph } = Typography;

export default function TrendTrackerLanding() {
  const [showFullImage, setShowFullImage] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const heroVideoRef = useRef<HTMLVideoElement>(null);

  // Function to check if element is in viewport
  const useIsInViewport = (ref: RefObject<HTMLVideoElement | null>) => {
    const [isIntersecting, setIsIntersecting] = useState(false);

    useEffect(() => {
      const observer = new IntersectionObserver(
        ([entry]) => setIsIntersecting(entry.isIntersecting),
        { threshold: 0.5 }
      );

      const currentRef = ref.current;

      if (currentRef) {
        observer.observe(currentRef);
      }

      return () => {
        if (currentRef) {
          observer.unobserve(currentRef);
        }
      };
    }, [ref]);

    return isIntersecting;
  };

  const isVideoInView = useIsInViewport(videoRef);

  // Play/pause video based on visibility
  useEffect(() => {
    if (videoRef.current) {
      if (isVideoInView) {
        videoRef.current.play().catch((error: Error) => {
          console.log('Autoplay prevented:', error);
        });
      } else {
        videoRef.current.pause();
      }
    }
  }, [isVideoInView]);

  // Handle video loading events
  useEffect(() => {
    const videoElement = videoRef.current;
    if (videoElement) {
      const handleLoadedData = () => {
        // setIsVideoLoading(false); // This line is removed
      };

      const handleWaiting = () => {
        // setIsVideoLoading(true); // This line is removed
      };

      const handlePlaying = () => {
        // setIsVideoLoading(false); // This line is removed
      };

      videoElement.addEventListener('loadeddata', handleLoadedData);
      videoElement.addEventListener('waiting', handleWaiting);
      videoElement.addEventListener('playing', handlePlaying);

      return () => {
        videoElement.removeEventListener('loadeddata', handleLoadedData);
        videoElement.removeEventListener('waiting', handleWaiting);
        videoElement.removeEventListener('playing', handlePlaying);
      };
    }
  }, []);

  // Handle hero video loading and autoplay
  useEffect(() => {
    const heroVideo = heroVideoRef.current;
    if (heroVideo) {
      const handleLoadedData = () => {
        console.log('Hero video loaded, attempting to play...');
        // Ensure properties are set before play attempt
        heroVideo.muted = true;
        heroVideo.playsInline = true as any;
        heroVideo.setAttribute('playsinline', '');
        heroVideo.setAttribute('muted', '');
        heroVideo.play().catch((error) => {
          console.log('Hero video autoplay prevented:', error);
          // Try to play on user interaction
          const playOnInteraction = () => {
            heroVideo.muted = true;
            heroVideo.setAttribute('muted', '');
            heroVideo.play().catch(console.log);
            document.removeEventListener('click', playOnInteraction);
            document.removeEventListener('touchstart', playOnInteraction);
          };
          document.addEventListener('click', playOnInteraction);
          document.addEventListener('touchstart', playOnInteraction);
        });
      };

      const handleCanPlay = () => {
        console.log('Hero video can play');
        heroVideo.muted = true;
        heroVideo.setAttribute('muted', '');
        heroVideo.play().catch((error) => {
          console.log('Hero video autoplay prevented on canplay:', error);
        });
      };

      const handleError = (e: Event) => {
        console.log('Hero video failed to load:', e);
      };

      const handleLoadStart = () => {
        console.log('Hero video load started');
      };

      const handlePlay = () => {
        console.log('Hero video is playing');
      };

      const handlePause = () => {
        console.log('Hero video is paused');
      };

      heroVideo.addEventListener('loadstart', handleLoadStart);
      heroVideo.addEventListener('loadeddata', handleLoadedData);
      heroVideo.addEventListener('canplay', handleCanPlay);
      heroVideo.addEventListener('play', handlePlay);
      heroVideo.addEventListener('pause', handlePause);
      heroVideo.addEventListener('error', handleError);

      return () => {
        heroVideo.removeEventListener('loadstart', handleLoadStart);
        heroVideo.removeEventListener('loadeddata', handleLoadedData);
        heroVideo.removeEventListener('canplay', handleCanPlay);
        heroVideo.removeEventListener('play', handlePlay);
        heroVideo.removeEventListener('pause', handlePause);
        heroVideo.removeEventListener('error', handleError);
      };
    }
  }, []);

  // const testimonialImages = [testimonial1, testimonial2, testimonial3];

  return (
    <div className="landing-page">
      <header>
        <div className="container">
          <a className="logo" href="./">
            <div
              style={{
                background: 'linear-gradient(to right, #2563eb, #a855f7)',
                color: '#fff',
                fontWeight: 600,
                padding: '8px 10px',
                borderRadius: '8px',
                fontSize: '14px',
                letterSpacing: '-0.02em',
                minWidth: '40px',
                textAlign: 'center',
              }}
            >
              TT
            </div>
            <div className="logo-text">TrendTracker</div>
          </a>
          <nav className="top-right-nav">
            <a href="#features-detailed">Features</a>
            <a href="#about">How It Works</a>
            <a href="#pricing">Pricing</a>
            <a href="#contact">Contact</a>
            {/* Get Started button removed */}
          </nav>
        </div>
      </header>
      <main>
        <section
          className="hero section-light"
          onClick={() => {
            if (heroVideoRef.current && heroVideoRef.current.paused) {
              heroVideoRef.current.play().catch(console.log);
            }
          }}
          style={{ cursor: 'pointer' }}
        >
          <video
            ref={heroVideoRef}
            className="hero-video"
            autoPlay
            muted
            loop
            playsInline
            poster={metroPoster}
            preload="auto"
          >
            <source src={metroVideo} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
          <div className="gradient-overlay"></div>
          <div className="container">
            <div className="hero-content">
              <div className="hero-badge">
                <Sparkles className="sparkle-icon" />
                <span>Multi-agent enabled</span>
              </div>
              {/* <p style={{ color: '#222222' }}>
                Affordable AI-powered platform that you will love
              </p> */}
              <h1>AI-Powered Market Intelligence</h1>
              <p className="subtitle">
                Real-time market analysis and forecasting powered by advanced AI
                models for data-driven decision making.
              </p>
              <div className="cta-buttons">
                {/* <Button
                  size="large"
                  onClick={() => setShowVideoModal(true)}
                  style={{ zIndex: 100 }}
                >
                  View Demo
                </Button> */}
                <Button
                  type="primary"
                  size="large"
                  href="/app"
                  style={{
                    backgroundColor: '#2563eb',
                    color: '#ffffff',
                    fontWeight: 500,
                    zIndex: 100,
                    border: 'none',
                    padding: '12px 32px',
                    fontSize: '15px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    height: 'auto',
                    lineHeight: 'normal',
                  }}
                >
                  <span>Start Free Trial</span>
                  <span style={{ fontSize: '18px' }}>→</span>
                </Button>
                <Button
                  onClick={() => setShowVideoModal(true)}
                  style={{
                    backgroundColor: 'transparent',
                    color: '#ffffff',
                    fontWeight: 500,
                    zIndex: 100,
                    border: '1px solid #4b5563',
                    padding: '12px 32px',
                    fontSize: '15px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    height: 'auto',
                    lineHeight: 'normal',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#1f2937';
                    e.currentTarget.style.borderColor = '#2563eb';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.borderColor = '#4b5563';
                  }}
                >
                  <span style={{ fontSize: '18px' }}>▶</span>
                  <span>Watch Demo</span>
                </Button>
              </div>

              {/* Dashboard Video Preview */}
              {/* <div
                className="dashboard-video-container"
                style={{
                  marginTop: '2rem',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: '0 8px 30px rgba(0, 0, 0, 0.2)',
                  width: '100%',
                  maxWidth: '900px',
                  margin: '2rem auto 0',
                  position: 'relative',
                  zIndex: 10,
                }}
              >
                {isVideoLoading && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      backgroundColor: 'rgba(0, 0, 0, 0.5)',
                      backdropFilter: 'blur(5px)',
                      zIndex: 12,
                      borderRadius: '12px',
                    }}
                  >
                    <Spin size="large" />
                  </div>
                )}
                <video
                  ref={videoRef}
                  muted
                  loop
                  playsInline
                  controls
                  poster={placeholder1}
                  style={{
                    width: '100%',
                    height: 'auto',
                    borderRadius: '12px',
                    display: 'block',
                  }}
                  onLoadStart={() => setIsVideoLoading(true)}
                >
                  <source src={demoVideo} type="video/mp4" />
                  Your browser does not support the video tag.
                </video> */}
              {/* <div
                  style={{
                    position: 'absolute',
                    top: '15px',
                    left: '15px',
                    background: 'rgba(0, 0, 0, 0.6)',
                    color: 'white',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    zIndex: 11,
                  }}
                >
                  Dashboard Preview
                </div> */}
              {/* </div> */}
            </div>
          </div>
        </section>
        <section id="features" className="section section-white">
          <div className="geometric-background"></div>
          <div className="container">
            <div className="features">
              <div className="feature-card">
                <BarChart2 className="feature-icon" />
                <h3>Sectors Analysis</h3>
                <p>
                  Comprehensive market analysis with real-time data and
                  sentiment tracking across multiple sectors.
                </p>
              </div>
              <div className="feature-card">
                <Newspaper className="feature-icon" />
                <h3>News Integration</h3>
                <p>
                  Stay updated with latest market news and trends through our
                  intelligent news aggregation system.
                </p>
              </div>
              <div className="feature-card">
                <TrendingUp className="feature-icon" />
                <h3>Trend Prediction</h3>
                <p>
                  Advanced AI models to predict market trends and provide
                  actionable insights for your business.
                </p>
              </div>
            </div>
          </div>
        </section>
        <section className="section how-it-works section-light">
          <div className="dot-pattern"></div>
          <div className="container">
            <Title level={2} className="section-title">
              How TrendTracker Works
            </Title>
            <div className="steps-container">
              {[
                // { icon: <Activity />, title: "1. Data Collection", description: "Real-time market data and news aggregation" },
                {
                  icon: <PieChart />,
                  title: '1. AI Analysis',
                  description: 'Advanced analysis using multiple AI models',
                },
                {
                  icon: <Target />,
                  title: '2. Insight Generation',
                  description: 'Actionable insights and recommendations',
                },
                {
                  icon: <TrendingUp />,
                  title: '3. Trend Tracking',
                  description: 'Continuous monitoring and updates',
                },
              ].map((step, index) => (
                <div key={index} className="step-card">
                  {step.icon}
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
        <section className="section parallax-section">
          <div className="container">
            <h2 className="section-heading">Empowering Business Decisions</h2>
            <Paragraph className="text-white">
              Our AI-powered platform gathers the strength of multiple AI models
              to provide you with actionable insights. Get daily alerts and
              easy-to-digest market signals without the complexity. Whether
              you're tracking market movements or monitoring your campaigns, our
              AI delivers clear, actionable clues that help you spot
              opportunities and stay ahead - all in just minutes a day.
            </Paragraph>
            {/* <Button
              type="primary"
              size="large"
              className="ghost-button"
              href="https://www.youtube.com/watch?v=7a9cyYUub9k"
            >
              Learn More about this topic <ArrowRight className="icon-right" />
            </Button> */}
          </div>
        </section>
        {/* <section className="section testimonials section-white">
          <div className="container">
            <Title level={2} className="section-title">Client Success Stories</Title>
            <div className="testimonials-grid">
              {[
                {
                  name: "Sarah Chen",
                  role: "Market Analyst",
                  company: "Global Investments Ltd",
                  text: "TrendTracker has revolutionized how we analyze market trends and make investment decisions."
                },
                {
                  name: "Michael Rodriguez",
                  role: "Strategy Director",
                  company: "Market Solutions Inc",
                  text: "The AI-powered insights have given us a competitive edge in identifying market opportunities."
                },
                {
                  name: "Emma Thompson",
                  role: "Research Head",
                  company: "TechMarket Analytics",
                  text: "The real-time news integration and trend analysis have become essential to our daily operations."
                }
              ].map((testimonial, i) => (
                <Card key={i} className="testimonial-card">
                  <div className="testimonial-header">
                    <Image
                      src={testimonialImages[i]}
                      alt={testimonial.name}
                      className="testimonial-avatar"
                      preview={false}
                    />
                    <div>
                      <h4>{testimonial.name}</h4>
                      <p>{testimonial.role}</p>
                      <p>{testimonial.company}</p>
                    </div>
                  </div>
                  <Paragraph>{testimonial.text}</Paragraph>
                </Card>
              ))}
            </div>
          </div>
        </section> */}

        <section id="features-detailed" className="section section-white">
          <div className="container">
            <Title level={2} className="section-title">
              Our Platform Features
            </Title>
            <div className="features-grid">
              {[
                {
                  icon: <Users />,
                  title: 'User-Friendly',
                  description:
                    'Intuitive interface designed for both beginners and experts.',
                  image: placeholder1,
                },
                {
                  icon: <Clock />,
                  title: 'Real-Time Values',
                  description:
                    'Get up-to-date information about the market situation.',
                  image: placeholder2,
                },
                {
                  icon: <Bot />,
                  title: 'Open Chat',
                  description:
                    'Chat with our AI agents to get answers to any question.',
                  image: placeholder4,
                },
                {
                  icon: <Building />,
                  title: 'Stock Market',
                  description:
                    'Peek into the stock market and get real-time updates.',
                  image: placeholder5,
                },
                {
                  icon: <AlertCircle />,
                  title: 'Important Insights',
                  description:
                    'Important market insights to guide your decisions.',
                  image: placeholder6,
                },
                {
                  icon: <Sparkles />,
                  title: 'Your own data',
                  description:
                    'Prioritize your own context and let the AI agents help you.',
                  image: placeholder8,
                },
              ].map((feature, index) => (
                <Card key={index} className="feature-detailed-card">
                  <div className="feature-content">
                    <div className="feature-header">
                      {feature.icon}
                      <h3>{feature.title}</h3>
                    </div>
                    <p>{feature.description}</p>
                  </div>
                  <Image
                    src={feature.image}
                    alt={feature.title}
                    className="feature-image"
                    preview={false}
                    style={{ borderRadius: '6px' }}
                  />
                </Card>
              ))}
            </div>
          </div>
        </section>
        <section id="about" className="section section-light">
          <div className="container">
            <Title level={2} className="section-title">
              About TrendTracker
            </Title>
            <div className="about-wrapper mobile-layout">
              {/* The image will float right and text will wrap around it */}
              <img
                src={extendedImage}
                alt="Extended TrendTracker Overview"
                className="about-image"
                onClick={() => setShowFullImage(true)}
                style={{ cursor: 'pointer' }}
              />
              {showFullImage && (
                <div
                  className="image-modal"
                  onClick={() => setShowFullImage(false)}
                  style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.9)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000,
                  }}
                >
                  <img
                    src={extendedImage}
                    alt="Extended TrendTracker Overview"
                    style={{
                      maxHeight: '90vh',
                      maxWidth: '90vw',
                      objectFit: 'contain',
                    }}
                  />
                </div>
              )}
              <div className="about-text">
                <p>
                  <strong>TrendTracker</strong> provides comprehensive market
                  intelligence through specialized AI agents that continuously
                  monitor, analyze, and interpret market dynamics in real-time,
                  providing insights that human analysts might miss. Our
                  ecosystem of <strong>AI agents</strong> specializes in
                  different market aspects - from sentiment analysis to pattern
                  recognition.
                </p>
                <p>
                  These agents work together, validating insights to ensure
                  accuracy and reliability. What sets us apart is our system's
                  ability to adapt and learn from market changes, ensuring our
                  clients stay ahead of trends with intelligence that gets
                  smarter every day.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="section pricing section-white">
          <div className="container">
            <Title level={2} className="section-title">
              Pricing Plan{' '}
              {/* <span style={{ fontSize: '14px' }}>
                (new admissions coming soon)
              </span> */}
            </Title>
            <div className="pricing-grid">
              {[
                // {
                //   name: 'Basic',
                //   price: '8',
                //   features: [
                //     'Market trend analysis',
                //     'Basic news integration',
                //     'Sporadic use',
                //     'Email support',
                //   ],
                // },
                // {
                //   name: 'Professional',
                //   price: '19',
                //   features: [
                //     'Advanced trend analysis',
                //     'Frequent use',
                //     'Custom reports',
                //   ],
                // },
                {
                  name: 'Enterprise',
                  price: 'Custom',
                  features: [
                    'Full platform access',
                    'Custom integrations',
                    'Dedicated account manager',
                    'API access',
                    'Priority support',
                  ],
                },
              ].map((plan) => (
                <Card
                  key={plan.name}
                  className={`pricing-card ${
                    plan.name === 'Professional' ? 'featured' : ''
                  }`}
                >
                  {/* {plan.name === 'Professional' && (
                    <div className="popular-badge">Most Popular</div>
                  )} */}
                  <h3>{plan.name}</h3>
                  <div className="price">
                    <span className="amount">${plan.price}</span>
                    {plan.price !== 'Custom' && (
                      <span className="period">/month</span>
                    )}
                  </div>
                  <ul className="features-list">
                    {plan.features.map((feature, i) => (
                      <li key={i}>{feature}</li>
                    ))}
                  </ul>
                  {/* <Button type={plan.name === 'Professional' ? 'primary' : 'default'} block>
                    Choose {plan.name}
                  </Button> */}
                </Card>
              ))}
            </div>
          </div>
        </section>
        <section className="section section-light text-center">
          <div className="container">
            <h2>Ready to Transform Your Market Analysis?</h2>
            <p>
              Join many companies using TrendTracker to stay ahead of market
              trends and make data-driven decisions.
            </p>
            <Button type="primary" size="large" href="/app">
              Start Free Trial
            </Button>
          </div>
        </section>
        <section id="contact" className="section section-white">
          <div className="container">
            <Title level={2} className="section-title">
              Get in Touch
            </Title>
            <div className="contact-grid temporal">
              {[
                // {
                //   icon: <Phone />,
                //   title: "Phone",
                //   content: "+420 000 000 000",
                //   link: "tel:+42000000000"
                // },
                {
                  icon: <Mail />,
                  title: 'Email',
                  content: 'info@trendtracker.work',
                  link: 'mailto:info@trendtracker.work',
                },
                // {
                //   icon: <MapPin />,
                //   title: 'Location',
                //   content: 'Prague, Czech Republic',
                //   link: 'https://www.google.com/maps/place/Prague,+Czechia',
                // },
              ].map((contact, index) => (
                <a key={index} href={contact.link} className="contact-card">
                  <div className="contact-icon">{contact.icon}</div>
                  <h3>{contact.title}</h3>
                  <p>{contact.content}</p>
                </a>
              ))}
            </div>
          </div>
        </section>
      </main>

      {/* Add this modal component */}
      {showVideoModal && (
        <div
          className="video-modal"
          onClick={() => setShowVideoModal(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
          }}
        >
          <video
            autoPlay
            controls
            style={{
              maxHeight: '90vh',
              maxWidth: '90vw',
              objectFit: 'contain',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <source src={demoVideo} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
      )}
    </div>
  );
}
